import React, { createContext, type SetStateAction } from "react";

import { noop } from "lodash";

import type { StreamNotesRef } from "../StreamNotes";

export interface CommentContext {
	threadKey: string;
	noteId: string;
	blockId: string;
}

export interface AskCopilotContext {
	noteId: string;
}

export interface MeetingBriefContext {
	noteId: string;
}

export interface StreamSideBarContextType {
	currentNoteId: string | null;
	setCurrentNoteId: (id: SetStateAction<string | null>) => void;
	agendaBuilderCurrentNoteId: string | null;
	setAgendaBuilderCurrentNoteId: (id: string | null) => void;
	agendaBuilderModeRegenerateKey: number;
	onGenerateAgendaBuilder?: () => void;
	doneGeneratingAIHeaders: boolean;
	setDoneGeneratingAIHeaders: (done: boolean) => void;
	commentContext?: CommentContext;
	setCommentContext: (commentContext?: CommentContext) => void;
	askCopilotContext?: AskCopilotContext;
	setAskCopilotContext: (askCopilotContext?: AskCopilotContext) => void;
	meetingBriefContext?: MeetingBriefContext;
	setMeetingBriefContext: (meetingBriefContext?: MeetingBriefContext) => void;
	streamNotesRef?: React.RefObject<StreamNotesRef>;
}

export const StreamSideBarContext = createContext<StreamSideBarContextType>({
	currentNoteId: null,
	setCurrentNoteId: noop,
	agendaBuilderCurrentNoteId: null,
	setAgendaBuilderCurrentNoteId: noop,
	agendaBuilderModeRegenerateKey: 0,
	onGenerateAgendaBuilder: undefined,
	doneGeneratingAIHeaders: true,
	setDoneGeneratingAIHeaders: noop,
	commentContext: undefined,
	setCommentContext: noop,
	askCopilotContext: undefined,
	setAskCopilotContext: noop,
	meetingBriefContext: undefined,
	setMeetingBriefContext: noop,
	streamNotesRef: undefined,
});

export function StreamSideBarContextProvider({
	children,
	currentNoteId,
	setCurrentNoteId,
	streamNotesRef,
}: {
	children: React.ReactNode;
	currentNoteId: string | null;
	setCurrentNoteId: (id: SetStateAction<string | null>) => void;
	streamNotesRef: React.RefObject<StreamNotesRef>;
}) {
	const [agendaBuilderCurrentNoteId, setAgendaBuilderCurrentNoteId] = React.useState<string | null>(null);
	const [agendaBuilderModeRegenerateKey, setAgendaBuilderModeRegenerateKey] = React.useState<number>(0);
	const onGenerateAgendaBuilder = React.useCallback(() => {
		setAgendaBuilderModeRegenerateKey(value => value + 1);
	}, []);
	const [doneGeneratingAIHeaders, setDoneGeneratingAIHeaders] = React.useState<boolean>(true);
	const [commentContext, setCommentContext] = React.useState<CommentContext | undefined>(undefined);
	const [askCopilotContext, setAskCopilotContext] = React.useState<AskCopilotContext | undefined>(undefined);
	const [meetingBriefContext, setMeetingBriefContext] = React.useState<MeetingBriefContext | undefined>(undefined);

	const value = React.useMemo(
		(): StreamSideBarContextType => ({
			currentNoteId,
			setCurrentNoteId,
			agendaBuilderCurrentNoteId,
			setAgendaBuilderCurrentNoteId,
			agendaBuilderModeRegenerateKey,
			onGenerateAgendaBuilder: agendaBuilderCurrentNoteId ? onGenerateAgendaBuilder : undefined,
			doneGeneratingAIHeaders,
			setDoneGeneratingAIHeaders,
			commentContext,
			setCommentContext,
			askCopilotContext,
			setAskCopilotContext,
			meetingBriefContext,
			setMeetingBriefContext,
			streamNotesRef,
		}),
		[
			agendaBuilderCurrentNoteId,
			agendaBuilderModeRegenerateKey,
			commentContext,
			currentNoteId,
			doneGeneratingAIHeaders,
			onGenerateAgendaBuilder,
			setCurrentNoteId,
			askCopilotContext,
			meetingBriefContext,
			streamNotesRef,
		],
	);
	return <StreamSideBarContext.Provider value={value}>{children}</StreamSideBarContext.Provider>;
}
